import axios from 'axios';

import assetProcessingActivitiesApi from 'config/api/assetProcessingActivities/assetProcessingActivities';
import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseAvailableProcessingActivitiesResponseForFE,
  parseOwnerListResponse,
  parseOwnerMineDetailForFE,
  parseOwnerMineMetricsResponseForFE,
  parseOwnerWorkspaceResponse,
} from './assetRopa.parsers';
import type {
  AssetProcessingActivitySaveInput,
  AssetProcessingActivitySubmitInput,
  AssetProcessingActivityUpdateInput,
  AssetRopaApi,
} from './assetRopa.types';
import type { AssetRopaOwnerFilterParams } from './assetRopaOwnerFilter.types';
import type { OwnerWorkspaceAsset } from './assetRopaWorkspace.types';

const buildOrganizationEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/asset-ropa/`;

const buildMineEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/asset-ropa/mine/`;

const buildMineMetricsEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/asset-ropa/mine/metrics/`;

const buildMineDetailEndpoint = (organizationId: number, assetRopaId: number) =>
  `api/v1/organizations/${organizationId}/asset-ropa/mine/${assetRopaId}/`;

const buildAssetRopaEndpoint = (organizationId: number, assetRopaId: number) =>
  `api/v1/organizations/${organizationId}/asset-ropa/${assetRopaId}/`;

export const assetRopaApi: AssetRopaApi = {
  getMine: async (organizationId, params?: AssetRopaOwnerFilterParams) => {
    const { data } = await axios.get(createUrlWithParams(buildMineEndpoint(organizationId), params));
    return { data: parseOwnerListResponse(data) };
  },
  getMineMetrics: async organizationId => {
    const { data } = await axios.get(buildMineMetricsEndpoint(organizationId));
    return { data: parseOwnerMineMetricsResponseForFE(data) };
  },
  getMineDetail: async (organizationId, assetRopaId) => {
    const { data } = await axios.get(buildMineDetailEndpoint(organizationId, assetRopaId));
    return { data: parseOwnerMineDetailForFE(data) };
  },
  getOwnerWorkspace: async (organizationId, params?: AssetRopaOwnerFilterParams) => {
    const { data } = await axios.get(createUrlWithParams(buildMineEndpoint(organizationId), params));
    return { data: parseOwnerWorkspaceResponse(data) };
  },
  getOrganizationWorkspace: async (organizationId, params?: AssetRopaOwnerFilterParams) => {
    const { data } = await axios.get(createUrlWithParams(buildOrganizationEndpoint(organizationId), params));
    return { data: parseOwnerWorkspaceResponse(data) };
  },
  getAvailableProcessingActivities: async (organizationId, assetRopaId, params = {}) => {
    const { data } = await axios.get(
      createUrlWithParams(`${buildAssetRopaEndpoint(organizationId, assetRopaId)}available-processing-activities/`, {
        ...(params.search ? { search: params.search } : {}),
        ...(params.page ? { page: params.page } : {}),
        ...(params.pageSize ? { page_size: params.pageSize } : {}),
      }),
    );

    return { data: parseAvailableProcessingActivitiesResponseForFE(data) };
  },
  addProcessingActivity: async (organizationId, assetRopaId, payload) => {
    const { data } = await assetProcessingActivitiesApi.createForAssetRopa(organizationId, assetRopaId, payload);
    return { data: data as OwnerWorkspaceAsset };
  },
  softDeleteProcessingActivity: async (organizationId, assetRopaId, apaId, payload) => {
    const { data } = await assetProcessingActivitiesApi.softDelete(organizationId, assetRopaId, apaId, payload);
    return { data: data as OwnerWorkspaceAsset };
  },
  getProcessingActivity: async (organizationId, apaId) => assetProcessingActivitiesApi.getDetail(organizationId, apaId),
  updateProcessingActivity: async (organizationId, apaId, payload: AssetProcessingActivityUpdateInput) =>
    assetProcessingActivitiesApi.update(organizationId, apaId, payload),
  saveProcessingActivity: async (organizationId, apaId, payload: AssetProcessingActivitySaveInput) =>
    assetProcessingActivitiesApi.save(organizationId, apaId, payload),
  submitProcessingActivity: async (organizationId, apaId, payload: AssetProcessingActivitySubmitInput = {}) =>
    assetProcessingActivitiesApi.submit(organizationId, apaId, payload),
  /** BE endpoint exists at runtime but is not yet published in OpenAPI (`schema.yaml`). */
  confirmNoProcessingActivities: async (organizationId, assetRopaId) => {
    await axios.post(`${buildAssetRopaEndpoint(organizationId, assetRopaId)}confirm-no-processing-activities/`, {});
  },
};

export default assetRopaApi;
